import { z } from "zod";
import { ConditionRatingsSchema, ValuationEstimateSchema } from "./offer.validator";
import { SNAPSHOT_ROWS } from "../valuation/index";

/** Akt notarialny w wykazie — kształt `OfferValuationRow`. */
const ValuationRowSchema = z.object({
  address: z.string().max(255),
  areaM2: z.number().nullable(),
  price: z.number().nullable(),
  ppm: z.number().nullable(),
  market: z.string().max(30),
  date: z.string().max(10),
});

/**
 * Zapisana wycena — komplet danych, z których da się odtworzyć raport (i wydruk PDF)
 * bez ponownego odpytywania RCN.
 *
 * Snapshot jest ZAMROŻONY: kotwice cenowe zmieniają się z każdą nową paczką aktów
 * w rejestrze, więc przeliczanie zapisanej wyceny „na świeżo" oznaczałoby, że dokument
 * wręczony klientowi w poniedziałek pokazuje w piątek inną kwotę.
 */
export const ValuationSnapshotSchema = z.object({
  address: z.string().trim().min(1).max(255),
  city: z.string().trim().max(120),
  areaM2: z.number().positive().max(100000),
  market: z.enum(["wtorny", "pierwotny"]),
  floor: z.number().int().min(-5).max(200).nullable(),
  hasElevator: z.boolean().nullable(),
  radiusM: z.number().int().positive().max(100000),
  monthsBack: z.number().int().positive().max(120),
  /** Zakres metrażu porównywanych lokali, gotowy do wyświetlenia (np. „56,3–68,8 m²"). */
  areaRangeLabel: z.string().max(60).nullable(),
  rejectedCount: z.number().int().nonnegative(),
  base: z.object({
    lowPpm: z.number(),
    medianPpm: z.number(),
    highPpm: z.number(),
    count: z.number().int().nonnegative(),
  }),
  ratings: ConditionRatingsSchema,
  estimate: ValuationEstimateSchema,
  // Wykaz jest przycięty do SNAPSHOT_ROWS już we froncie; limit tutaj jest bramką
  // na rozmiar rekordu, a nie regulacją prezentacji.
  rows: z.array(ValuationRowSchema).max(SNAPSHOT_ROWS),
});

export type ValuationSnapshot = z.infer<typeof ValuationSnapshotSchema>;

/**
 * POST /v1/valuations — zapis świeżo policzonej wyceny.
 *
 * Świadomie BEZ `projectId`: kolumna `valuations.project_id` jest rezerwą na przypięcie
 * wyceny do oferty, ale przyjęcie uuid z żądania bez sprawdzenia właściciela pozwalałoby
 * podpiąć wycenę pod cudzą ofertę. Pole wejdzie razem z odczytem po projekcie.
 */
export const CreateValuationSchema = z.object({
  snapshot: ValuationSnapshotSchema,
});

export type CreateValuationInput = z.infer<typeof CreateValuationSchema>;

/**
 * PUT /v1/valuations/:id — nadpisanie snapshotu.
 *
 * Istnieje, bo agent po zobaczeniu wyniku rusza suwakami stanu, a to zmienia kwotę.
 * Bez aktualizacji lista pokazywałaby wycenę neutralną, której nikt nie widział
 * na ekranie dłużej niż sekundę.
 */
export const UpdateValuationSchema = z.object({
  snapshot: ValuationSnapshotSchema,
});

export type UpdateValuationInput = z.infer<typeof UpdateValuationSchema>;
